/* ===================================================================
   Shared HTTP + token layer for the Rhody Reviews api/v1 client.

   The backend (engine/api/v1) is a Bearer-token machine API. Auth header
   = "Authorization: Bearer rr_<prefix>_<random>" (an API key issued from
   the dashboard; see engine/api/v1/auth.py). The token's client_id scopes
   every query server-side.

   Base URL comes from VITE_API_BASE (e.g. http://localhost:8003 in dev,
   https://app.rhodyrep.com in prod). No trailing slash needed.

   AUTH MODEL (Hopper, 2026-06-09 -- UPDATED):
   User login/signup now runs on SUPABASE AUTH (Steve's confirmed
   approach), wired in src/auth/ + src/lib/supabase.ts. This client still
   talks to the Python api/v1 (Bearer rr_ API key) for review/customer/
   transaction data.

   GATE-PREP NOTE (2026): this module is the shared request()/token core
   extracted verbatim from the former single lib/api.ts during the
   per-domain split. The domain modules (lib/api/<domain>.ts) import
   request() + helpers from here; the barrel (lib/api/index.ts) re-exports
   everything so the existing importers ("../lib/api") are unchanged.
   =================================================================== */

// -------------------------------------------------------------------
// Base config + token storage
// -------------------------------------------------------------------
const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

// The OAuth flow server (engine/signup/oauth_flow.py) may run on its own
// origin/port in dev (default 8002). In prod both sit behind rhodyreviews.com.
const OAUTH_BASE: string = (
  import.meta.env.VITE_OAUTH_BASE ?? API_BASE
).replace(/\/+$/, '');

const TOKEN_KEY = 'rhody_api_token';

/* The rr_ data-plane token, mirrored in-memory so reads are reactive.
   localStorage alone is not reactive: a React hook that called getToken()
   on first paint would never re-enable when the token lands a tick later.
   We keep an in-memory copy + a tiny subscriber set so useApiToken() (in
   hooks/) can re-render the data hooks the moment a token is issued. */
let _tokenCache: string | null = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

const _tokenSubs = new Set<() => void>();

export function getToken(): string | null {
  return _tokenCache;
}

export function setToken(token: string | null): void {
  _tokenCache = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode, etc.) -- non-fatal */
  }
  // Notify subscribers (data hooks) so disabled queries re-enable.
  for (const fn of _tokenSubs) {
    try {
      fn();
    } catch {
      /* a bad subscriber must not break setToken */
    }
  }
}

/* useSyncExternalStore plumbing for hooks/useApiToken. */
export function subscribeToken(cb: () => void): () => void {
  _tokenSubs.add(cb);
  return () => {
    _tokenSubs.delete(cb);
  };
}
export function getTokenSnapshot(): string | null {
  return _tokenCache;
}

// -------------------------------------------------------------------
// Error type
// -------------------------------------------------------------------
export class ApiError extends Error {
  status: number;
  detail?: string;
  /* The full parsed error body, so callers can read endpoint-specific fields
     (e.g. draft_id on a 409 draft_exists) beyond error/detail. */
  body?: Record<string, unknown>;
  constructor(status: number, message: string, detail?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

// -------------------------------------------------------------------
// 401 recovery hook (transparent re-mint + auto-retry).
//
// A stored rr_ token can die under a long-lived tab: the server stamps a 24h
// TTL on session keys (engine SESSION_KEY_TTL_SECONDS), and every new mint
// revokes the prior one. When request() below hits a 401 on the stored token
// it asks the registered refresher to mint a FRESH rr_ token from the CURRENT
// Supabase session, then retries the original call ONCE -- so a still-signed-in
// owner never sees the failure. The refresher lives in ./reauth (it needs the
// Supabase client + the exchange endpoint); it is INJECTED here so this module
// stays dependency-free and import-cycle-free. It returns the fresh rr_ token,
// or null when recovery is impossible (dead/absent Supabase session -- in which
// case ./reauth also signs the user out so the route guard sends them to /auth).
// -------------------------------------------------------------------
export type TokenRefresher = () => Promise<string | null>;
let _refresher: TokenRefresher | null = null;
export function registerTokenRefresher(fn: TokenRefresher | null): void {
  _refresher = fn;
}

// -------------------------------------------------------------------
// Core request helper
// -------------------------------------------------------------------
export type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  /* Override the stored token for a specific call (rarely needed). */
  token?: string | null;
  signal?: AbortSignal;
  /* Internal: set on the single auto-retry that follows a transparent 401
     re-mint, so a persistently-401ing token cannot loop. Do NOT set from
     callers. */
  _retried?: boolean;
}

export function buildUrl(path: string, query?: Query): string {
  const url = new URL(
    path.replace(/^\//, ''),
    (API_BASE || window.location.origin) + '/',
  );
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const token = opts.token !== undefined ? opts.token : getToken();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    throw new ApiError(0, 'network_error', e instanceof Error ? e.message : String(e));
  }

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    // A 401 on a request that DID carry our rr_ token means that token is dead
    // (expired 24h TTL, or revoked because a newer session-token mint
    // superseded it). Try to transparently re-mint a fresh rr_ token from the
    // CURRENT Supabase session and retry this request exactly once, so a
    // still-signed-in owner never sees the failure. If recovery is impossible
    // (the Supabase session is also gone), the refresher clears the token AND
    // signs out, and we fall through to a truthful 401 that the UI renders as
    // "session expired, sign in again." We only do this for the stored token
    // (not an explicit per-call override), never for the unauthenticated health
    // check (token === null), and never on the already-retried follow-up.
    if (res.status === 401 && token && opts.token === undefined && !opts._retried) {
      const fresh = _refresher ? await _refresher() : null;
      if (fresh) {
        return request<T>(path, { ...opts, _retried: true });
      }
      setToken(null);
    }
    const obj = (data ?? {}) as { error?: string; detail?: string };
    throw new ApiError(
      res.status,
      obj.error ?? `http_${res.status}`,
      obj.detail,
      typeof data === 'object' && data ? (data as Record<string, unknown>) : undefined,
    );
  }
  return data as T;
}

export { API_BASE, OAUTH_BASE };
